import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  X,
  ChevronDown,
  Calendar,
  Download,
  Filter,
  FileText,
  FileSpreadsheet,
  BarChart3,
  Package,
  RefreshCw,
  SortAsc,
  Grid,
  Hash,
  Database,
  CheckCircle,
  ChevronUp,
  ChevronRight,
  ChefHat,
  Users,
  Building2,
  Eye,
  EyeOff
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI, chefsAPI } from '../services/api';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  format,
  parseISO,
  isSameDay,
  addHours,
} from 'date-fns';
import { arSA, enUS } from 'date-fns/locale';

// ==============================
// === INTERFACES ===
// ==============================
interface Chef {
  _id: string;
  user: {
    _id: string;
    name: string;
    nameEn: string;
    username: string;
    email: string;
  };
  department: Array<{
    _id: string;
    name: string;
    nameEn: string;
    code: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface ProductData {
  id: string;
  name: string;
  code: string;
  totalAssigned: number;
  totalCompleted: number;
  unit: string;
  department?: string;
  departmentId?: string;
}

interface ChefProductionData {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  departments: Array<{
    id: string;
    name: string;
    nameEn: string;
    code: string;
  }>;
  status: string;
  
  // الإحصائيات
  totalAssignedQuantity: number;
  totalCompletedQuantity: number;
  completionRate: number;
  
  // المنتجات
  products: {
    [productId: string]: ProductData;
  };
}

// ==============================
// === UTILITY FUNCTIONS ===
// ==============================
const formatNumberWithComma = (num: number): string => {
  if (isNaN(num) || num === null || num === undefined) return '0';
  
  if (num % 1 !== 0) {
    const rounded = Math.round(num * 100) / 100;
    const parts = rounded.toString().split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const decimalPart = parts[1] ? parts[1].padEnd(2, '0').substring(0, 2) : '00';
    return `${integerPart}.${decimalPart}`;
  }
  
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const toArabicNumerals = (number: string | number): string => {
  if (number === null || number === undefined) return '0';
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  
  const numberStr = String(number);
  const parts = numberStr.split('.');
  
  const integerPart = parts[0].replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
  
  if (parts[1]) {
    const decimalPart = parts[1].replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
    return `${integerPart}.${decimalPart}`;
  }
  
  return integerPart;
};

const formatNumber = (num: number, isRtl: boolean): string => {
  const formatted = formatNumberWithComma(num);
  return isRtl ? toArabicNumerals(formatted) : formatted;
};

const formatPercentage = (num: number, isRtl: boolean): string => {
  const formatted = Math.round(num * 100) / 100;
  return `${formatNumber(formatted, isRtl)}%`;
};

const fixArabicText = (text: string): string => {
  if (!text || typeof text !== 'string') return text || '';
  
  let fixedText = text.trim();
  
  fixedText = fixedText
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  
  return fixedText;
};

const calculatePeriodDates = (period: string) => {
  const now = new Date();
  let start: Date, end: Date;

  switch (period) {
    case 'today':
      start = addHours(startOfDay(now), 12);
      end = endOfDay(now);
      break;
    case 'yesterday':
      const yesterday = subDays(now, 1);
      start = addHours(startOfDay(yesterday), 12);
      end = endOfDay(yesterday);
      break;
    case 'week':
      start = startOfDay(subDays(now, 6));
      end = endOfDay(now);
      break;
    case 'month':
      start = startOfDay(subMonths(now, 1));
      end = endOfDay(now);
      break;
    default:
      start = addHours(startOfDay(now), 12);
      end = endOfDay(now);
  }

  return { start, end };
};

const getQuantityColor = (quantity: number, isCompleted: boolean = false): string => {
  if (quantity === 0) return 'text-gray-400';
  
  if (isCompleted) {
    if (quantity <= 10) return 'text-emerald-600';
    if (quantity <= 50) return 'text-green-600';
    if (quantity <= 100) return 'text-green-700';
    return 'text-emerald-800';
  } else {
    if (quantity <= 10) return 'text-amber-600';
    if (quantity <= 50) return 'text-orange-600';
    if (quantity <= 100) return 'text-orange-700';
    return 'text-amber-800';
  }
};

const getQuantityBgColor = (quantity: number, isCompleted: boolean = false): string => {
  if (quantity === 0) return 'bg-gray-50';
  
  if (isCompleted) {
    if (quantity <= 10) return 'bg-emerald-50';
    if (quantity <= 50) return 'bg-green-50';
    if (quantity <= 100) return 'bg-green-100';
    return 'bg-emerald-100';
  } else {
    if (quantity <= 10) return 'bg-amber-50';
    if (quantity <= 50) return 'bg-orange-50';
    if (quantity <= 100) return 'bg-orange-100';
    return 'bg-amber-100';
  }
};

// ==============================
// === PDF FONT LOADER ===
// ==============================
const loadAmiriFont = async (doc: jsPDF): Promise<boolean> => {
  const fontName = 'Amiri';
  try {
    const [regularRes, boldRes] = await Promise.all([
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@main/fonts/Amiri-Regular.ttf'),
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@main/fonts/Amiri-Bold.ttf'),
    ]);
    const [regularFontBytes, boldFontBytes] = await Promise.all([
      regularRes.arrayBuffer(),
      boldRes.arrayBuffer(),
    ]);
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return window.btoa(binary);
    };
    doc.addFileToVFS(`${fontName}-normal.ttf`, arrayBufferToBase64(regularFontBytes));
    doc.addFont(`${fontName}-normal.ttf`, fontName, 'normal');
    doc.addFileToVFS(`${fontName}-bold.ttf`, arrayBufferToBase64(boldFontBytes));
    doc.addFont(`${fontName}-bold.ttf`, fontName, 'bold');
    return true;
  } catch (error) {
    console.warn('Failed to load Amiri font. Using fallback.');
    return false;
  }
};

// ==============================
// === دالة مساعدة لإنشاء اسم الملف ===
// ==============================
const generateFileName = (
  viewType: 'chefs' | 'departments',
  periodLabel: string,
  selectedChefsMulti: string[],
  selectedDepartmentsMulti: string[],
  searchQuery: string,
  sortBy: string,
  showCompleted: boolean,
  isRtl: boolean,
  fileType: 'pdf' | 'xlsx'
): string => {
  const dateStr = new Date().toISOString().split('T')[0];
  const timeStr = new Date().toTimeString().split(' ')[0].replace(/:/g, '-');
  
  const parts = [];
  
  if (viewType === 'chefs') {
    parts.push(isRtl ? 'تقرير_الشيفات' : 'Chefs_Report');
  } else {
    parts.push(isRtl ? 'تقرير_الأقسام' : 'Departments_Report');
  }
  
  const periodText = periodLabel
    .replace(/\s+/g, '_')
    .replace(/\//g, '-')
    .substring(0, 20);
  parts.push(periodText);
  
  if (!showCompleted) {
    parts.push(isRtl ? 'بدون_مكتمل' : 'No_Completed');
  }
  
  if (searchQuery && searchQuery.trim().length > 0 && searchQuery.trim().length < 15) {
    const searchText = searchQuery
      .replace(/\s+/g, '_')
      .substring(0, 10);
    parts.push(`بحث_${searchText}`);
  }
  
  parts.push(dateStr);
  parts.push(timeStr);
  
  let fileName = parts.join('_');
  fileName = fileName.replace(/[^a-zA-Z0-9_\u0600-\u06FF\-\.]/g, '');
  
  return `${fileName}.${fileType}`;
};

// ==============================
// === تصدير منتجات القسم فقط ===
// ==============================
const exportDepartmentProducts = async (
  departmentName: string,
  departmentProducts: ProductData[],
  isRtl: boolean,
  fileType: 'pdf' | 'xlsx'
) => {
  const toastId = 'dept-products-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء الملف...' : 'Generating file...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    if (departmentProducts.length === 0) {
      toast.update(toastId, {
        render: isRtl ? 'لا توجد منتجات في هذا القسم' : 'No products in this department',
        type: 'warning',
        autoClose: 3000,
      });
      return;
    }

    if (fileType === 'pdf') {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const fontLoaded = await loadAmiriFont(doc);
      const fontName = fontLoaded ? 'Amiri' : 'helvetica';
      const pageWidth = doc.internal.pageSize.width;
      const margin = 15;

      doc.setFont(fontName, 'bold');
      doc.setFontSize(16);
      doc.setTextColor(31, 41, 55);
      const title = isRtl 
        ? `تقرير منتجات قسم: ${fixArabicText(departmentName)}`
        : `Department Products Report: ${departmentName}`;
      doc.text(title, pageWidth / 2, 20, { align: 'center' });

      doc.setFont(fontName, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      
      const date = new Date().toLocaleDateString(isRtl ? 'ar-SA' : 'en-US');
      const infoText = isRtl
        ? `عدد المنتجات: ${departmentProducts.length} | التاريخ: ${date}`
        : `Products Count: ${departmentProducts.length} | Date: ${date}`;
      
      doc.text(infoText, pageWidth / 2, 30, { align: 'center' });

      let yPos = 40;

      const headers = isRtl
        ? ['الكود', 'اسم المنتج', 'الوحدة', 'المطلوب']
        : ['Code', 'Product Name', 'Unit', 'Required'];

      const body = departmentProducts.map(product => {
        const productName = fixArabicText(product.name || '');
        const productCode = product.code || '-';
        const productUnit = fixArabicText(product.unit || '-');
        const requiredQty = formatNumberWithComma(product.totalAssigned || 0);
        
        if (isRtl) {
          return [
            productCode,
            productName,
            productUnit,
            requiredQty
          ];
        } else {
          return [
            productCode,
            product.name || '-',
            product.unit || '-',
            requiredQty
          ];
        }
      });

      const totalRequired = departmentProducts.reduce((sum, product) => 
        sum + (product.totalAssigned || 0), 0
      );

      body.push(
        isRtl
          ? ['', 'الإجمالي', '', formatNumberWithComma(totalRequired)]
          : ['', 'Total', '', formatNumberWithComma(totalRequired)]
      );

      autoTable(doc, {
        head: [headers],
        body: body,
        startY: yPos,
        margin: { left: margin, right: margin },
        theme: 'grid',
        styles: {
          font: fontName,
          fontSize: 11,
          textColor: [31, 41, 55],
          lineColor: [209, 213, 219],
          lineWidth: 0.5,
          cellPadding: 6,
          overflow: 'linebreak',
          halign: isRtl ? 'right' : 'left',
          valign: 'middle'
        },
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontSize: 12,
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          cellPadding: 8,
          lineWidth: 0.5,
          lineColor: [37, 99, 235],
          font: fontName
        },
        bodyStyles: {
          fontSize: 11,
          halign: isRtl ? 'right' : 'left',
          valign: 'middle',
          cellPadding: 6,
          lineWidth: 0.5,
          overflow: 'linebreak',
          font: fontName
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251]
        },
        columnStyles: {
          0: { cellWidth: 25, halign: 'center' },
          1: { cellWidth: 70, halign: isRtl ? 'right' : 'left' },
          2: { cellWidth: 25, halign: 'center' },
          3: { cellWidth: 25, halign: 'center' }
        },
        didParseCell: (data: any) => {
          if (data.row.index === departmentProducts.length) {
            data.cell.styles.fillColor = [245, 158, 11];
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.halign = 'center';
          }
          
          if (isRtl && typeof data.cell.text === 'string') {
            data.cell.text = data.cell.text.replace(/\d+/g, (match: string) => 
              toArabicNumerals(match)
            );
          }
          
          if (data.column.index === 3) {
            data.cell.styles.halign = 'center';
            data.cell.styles.fontStyle = 'bold';
          }
          
          if (isRtl && data.cell.text && typeof data.cell.text === 'string') {
            data.cell.text = fixArabicText(data.cell.text);
          }
        }
      });

      const finalY = (doc as any).lastAutoTable.finalY || yPos + 100;
      doc.setFontSize(9);
      doc.setTextColor(107, 114, 128);
      const footer = isRtl 
        ? '* تم إنشاء هذا التقرير بواسطة نظام إدارة الجوديا'
        : '* This report was generated by aljodia Management System';
      doc.text(footer, pageWidth / 2, finalY + 10, { align: 'center' });

      const safeDeptName = departmentName.replace(/[^a-zA-Z0-9\u0600-\u06FF\s]/g, '');
      const fileName = isRtl
        ? `منتجات_${safeDeptName}_${new Date().toISOString().split('T')[0]}.pdf`
        : `products_${safeDeptName}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      doc.save(fileName);

    } else {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(isRtl ? 'منتجات القسم' : 'Department Products', {
        views: [{ rightToLeft: isRtl }]
      });

      worksheet.mergeCells('A1:D1');
      const titleRow = worksheet.getRow(1);
      titleRow.height = 30;
      const titleCell = titleRow.getCell(1);
      titleCell.value = isRtl 
        ? `تقرير منتجات قسم: ${fixArabicText(departmentName)}`
        : `Department Products Report: ${departmentName}`;
      titleCell.font = { 
        size: 16, 
        bold: true, 
        color: { argb: '1F2937' },
        name: isRtl ? 'Arial' : 'Calibri'
      };
      titleCell.alignment = { 
        horizontal: 'center', 
        vertical: 'middle',
        wrapText: true 
      };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DBEAFE' }
      };

      worksheet.mergeCells('A2:D2');
      const infoRow = worksheet.getRow(2);
      infoRow.height = 24;
      const infoCell = infoRow.getCell(1);
      const date = new Date().toLocaleDateString(isRtl ? 'ar-SA' : 'en-US');
      infoCell.value = isRtl
        ? `عدد المنتجات: ${departmentProducts.length} | التاريخ: ${date}`
        : `Products Count: ${departmentProducts.length} | Date: ${date}`;
      infoCell.font = { 
        size: 11, 
        color: { argb: '6B7280' },
        name: isRtl ? 'Arial' : 'Calibri'
      };
      infoCell.alignment = { 
        horizontal: 'center', 
        vertical: 'middle',
        wrapText: true 
      };

      worksheet.addRow([]);

      const headers = isRtl
        ? ['الكود', 'اسم المنتج', 'الوحدة', 'المطلوب']
        : ['Code', 'Product Name', 'Unit', 'Required'];
      
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 28;
      headerRow.eachCell((cell, colNumber) => {
        cell.font = { 
          bold: true, 
          size: 12, 
          color: { argb: 'FFFFFF' },
          name: isRtl ? 'Arial' : 'Calibri'
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '3B82F6' }
        };
        cell.alignment = { 
          horizontal: 'center', 
          vertical: 'middle',
          wrapText: true 
        };
        cell.border = {
          top: { style: 'thin', color: { argb: '1D4ED8' } },
          left: { style: 'thin', color: { argb: '1D4ED8' } },
          bottom: { style: 'thin', color: { argb: '1D4ED8' } },
          right: { style: 'thin', color: { argb: '1D4ED8' } }
        };
      });

      departmentProducts.forEach((product, index) => {
        const rowData = [
          product.code || '-',
          fixArabicText(product.name || ''),
          fixArabicText(product.unit || '-'),
          product.totalAssigned || 0
        ];

        const row = worksheet.addRow(rowData);
        row.height = 24;

        row.eachCell((cell, colNumber) => {
          let horizontalAlign: ExcelJS.Alignment['horizontal'] = 'center';
          
          if (colNumber === 2) {
            horizontalAlign = isRtl ? 'right' : 'left';
          } else if (colNumber === 4) {
            horizontalAlign = 'center';
          } else {
            horizontalAlign = 'center';
          }
          
          cell.alignment = { 
            horizontal: horizontalAlign, 
            vertical: 'middle',
            wrapText: true 
          };
          
          cell.border = {
            top: { style: 'thin', color: { argb: 'E5E7EB' } },
            left: { style: 'thin', color: { argb: 'E5E7EB' } },
            bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
            right: { style: 'thin', color: { argb: 'E5E7EB' } }
          };

          if (index % 2 === 0) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'F9FAFB' }
            };
          }

          if (colNumber === 4) {
            cell.numFmt = '#,##0';
            cell.font = { 
              bold: true, 
              color: { argb: 'D97706' },
              name: isRtl ? 'Arial' : 'Calibri'
            };
          }
          
          if (isRtl && colNumber === 2) {
            cell.font = { 
              size: 11,
              name: 'Arial'
            };
          }
        });
      });

      const totalRequired = departmentProducts.reduce((sum, product) => 
        sum + (product.totalAssigned || 0), 0
      );

      const totalRow = worksheet.addRow([
        '',
        isRtl ? 'الإجمالي' : 'Total',
        '',
        totalRequired
      ]);
      
      totalRow.height = 28;
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { 
          bold: true, 
          size: 12, 
          color: { argb: 'FFFFFF' },
          name: isRtl ? 'Arial' : 'Calibri'
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F59E0B' }
        };
        cell.alignment = { 
          horizontal: 'center', 
          vertical: 'middle',
          wrapText: true 
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'D97706' } },
          left: { style: 'thin', color: { argb: 'D97706' } },
          bottom: { style: 'thin', color: { argb: 'D97706' } },
          right: { style: 'thin', color: { argb: 'D97706' } }
        };
        
        if (colNumber === 4) {
          cell.numFmt = '#,##0';
        }
      });

      worksheet.columns = [
        { width: 20 },
        { width: 50 },
        { width: 20 },
        { width: 20 }
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      
      const safeDeptName = departmentName.replace(/[^a-zA-Z0-9\u0600-\u06FF\s]/g, '');
      const fileName = isRtl
        ? `منتجات_${safeDeptName}_${new Date().toISOString().split('T')[0]}.xlsx`
        : `products_${safeDeptName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      saveAs(blob, fileName);
    }

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير بنجاح!' : 'Exported successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Export error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل التصدير. حاول مرة أخرى.' : 'Export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === PDF EXPORT - FIXED (بدون تكرار) ===
// ==============================
const exportToPDF = async (
  chefsData: ChefProductionData[],
  allProducts: ProductData[],
  displayedDepartments: any[],
  viewType: 'chefs' | 'departments',
  periodLabel: string,
  sortBy: string,
  selectedChefsMulti: string[],
  selectedDepartmentsMulti: string[],
  searchQuery: string,
  isRtl: boolean,
  language: string,
  showCompleted: boolean,
  
  calculateProductTotals: (product: ProductData) => { assignedTotal: number, completedTotal: number },
  calculateChefTotals: (chef: ChefProductionData) => { assignedTotal: number, completedTotal: number },
  getProductValuesForChef: (chef: ChefProductionData, product: ProductData) => { assigned: number, completed: number },
  getChefsByDepartment: (departmentName: string) => ChefProductionData[],
  getProductsByDepartment: (departmentName: string) => ProductData[]
) => {
  const toastId = 'pdf-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف PDF...' : 'Generating PDF...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const fontLoaded = await loadAmiriFont(doc);
    const fontName = fontLoaded ? 'Amiri' : 'helvetica';
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;

    const headerBgColor = [245, 158, 11];
    const totalRowColor = [245, 158, 11];
    const altRowColor = [249, 250, 251];
    const borderColor = [229, 231, 235];
    const textColor = [31, 41, 55];
    const lightTextColor = [107, 114, 128];

    doc.setFont(fontName, 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...textColor);
    const title = viewType === 'chefs' 
      ? (isRtl ? 'تقرير إنتاج الشيفات' : 'Chefs Production Report')
      : (isRtl ? 'تقرير إنتاج الأقسام' : 'Departments Production Report');
    doc.text(title, pageWidth / 2, 15, { align: 'center' });

    doc.setFont(fontName, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...lightTextColor);
    
    const infoParts = [];
    infoParts.push(periodLabel);
    infoParts.push(viewType === 'chefs' 
      ? (isRtl ? 'عرض حسب الشيفات' : 'View: By Chefs')
      : (isRtl ? 'عرض حسب الأقسام' : 'View: By Departments'));
    
    if (!showCompleted) {
      infoParts.push(isRtl ? 'بدون المكتمل' : 'Without Completed');
    }
    
    const infoText = infoParts.join('  |  ');
    doc.text(infoText, pageWidth / 2, 25, { align: 'center' });

    let yPos = 35;

    if (viewType === 'chefs' && chefsData.length === 0) {
      doc.setFont(fontName, 'normal');
      doc.setFontSize(12);
      doc.setTextColor(...lightTextColor);
      const noDataText = isRtl ? 'لا توجد بيانات للشيفات' : 'No chefs data available';
      doc.text(noDataText, pageWidth / 2, yPos + 20, { align: 'center' });
    } else if (viewType === 'departments' && displayedDepartments.length === 0) {
      doc.setFont(fontName, 'normal');
      doc.setFontSize(12);
      doc.setTextColor(...lightTextColor);
      const noDataText = isRtl ? 'لا توجد بيانات للأقسام' : 'No departments data available';
      doc.text(noDataText, pageWidth / 2, yPos + 20, { align: 'center' });
    } else {
      let headers: string[];
      let body: any[][];

      if (viewType === 'chefs') {
        if (isRtl) {
          headers = [
            'الكود',
            'المنتج',
            'القسم'
          ];
          
          chefsData.forEach(chef => {
            if (showCompleted) {
              headers.push(fixArabicText(chef.name));
            } else {
              headers.push(fixArabicText(chef.name));
            }
          });
          
          if (showCompleted) {
            headers.push('الإجمالي');
          } else {
            headers.push('إجمالي المطلوب');
          }
          
        } else {
          headers = [
            'Code',
            'Product',
            'Department'
          ];
          
          chefsData.forEach(chef => {
            headers.push(chef.name);
          });
          
          if (showCompleted) {
            headers.push('Total');
          } else {
            headers.push('Total Assigned');
          }
        }

        body = allProducts.map(product => {
          let row;
          
          if (isRtl) {
            row = [
              product.code || '-',
              fixArabicText(product.name),
              fixArabicText(product.department || '-')
            ];
            
            chefsData.forEach(chef => {
              const values = getProductValuesForChef(chef, product);
              if (showCompleted) {
                row.push(formatNumberWithComma(values.assigned));
              } else {
                row.push(formatNumberWithComma(values.assigned));
              }
            });
            
            if (showCompleted) {
              const totals = calculateProductTotals(product);
              row.push(formatNumberWithComma(totals.assignedTotal));
            } else {
              row.push(formatNumberWithComma(calculateProductTotals(product).assignedTotal));
            }
            
          } else {
            row = [
              product.code || '-',
              product.name,
              product.department || '-'
            ];
            
            chefsData.forEach(chef => {
              const values = getProductValuesForChef(chef, product);
              row.push(formatNumberWithComma(values.assigned));
            });
            
            row.push(formatNumberWithComma(calculateProductTotals(product).assignedTotal));
          }
          
          return row;
        });
        
        if (isRtl) {
          const totalRow = [
            '',
            'الإجمالي',
            '',
            ...chefsData.map(chef => {
              const totals = calculateChefTotals(chef);
              return formatNumberWithComma(totals.assignedTotal);
            }),
            formatNumberWithComma(chefsData.reduce((sum, chef) => sum + calculateChefTotals(chef).assignedTotal, 0))
          ];
          
          body.push(totalRow);
        } else {
          const totalRow = [
            '',
            'Total',
            '',
            ...chefsData.map(chef => {
              const totals = calculateChefTotals(chef);
              return formatNumberWithComma(totals.assignedTotal);
            }),
            formatNumberWithComma(chefsData.reduce((sum, chef) => sum + calculateChefTotals(chef).assignedTotal, 0))
          ];
          
          body.push(totalRow);
        }
        
        if (showCompleted) {
          const completedHeaderRow = isRtl 
            ? ['', '', 'المكتمل', ...chefsData.map(() => ''), '']
            : ['', '', 'Completed', ...chefsData.map(() => ''), ''];
          
          body.splice(0, 0, completedHeaderRow);
          
          const completedBody = allProducts.map(product => {
            let row;
            
            if (isRtl) {
              row = [
                '',
                '',
                ''
              ];
              
              chefsData.forEach(chef => {
                const values = getProductValuesForChef(chef, product);
                row.push(formatNumberWithComma(values.completed));
              });
              
              row.push(formatNumberWithComma(calculateProductTotals(product).completedTotal));
              
            } else {
              row = [
                '',
                '',
                ''
              ];
              
              chefsData.forEach(chef => {
                const values = getProductValuesForChef(chef, product);
                row.push(formatNumberWithComma(values.completed));
              });
              
              row.push(formatNumberWithComma(calculateProductTotals(product).completedTotal));
            }
            
            return row;
          });
          
          const completedTotalRow = isRtl 
            ? ['', '', '', 
               ...chefsData.map(chef => formatNumberWithComma(calculateChefTotals(chef).completedTotal)),
               formatNumberWithComma(chefsData.reduce((sum, chef) => sum + calculateChefTotals(chef).completedTotal, 0))]
            : ['', '', '',
               ...chefsData.map(chef => formatNumberWithComma(calculateChefTotals(chef).completedTotal)),
               formatNumberWithComma(chefsData.reduce((sum, chef) => sum + calculateChefTotals(chef).completedTotal, 0))];
          
          completedBody.push(completedTotalRow);
          
          body = [...completedBody, ...body];
        }
        
      } else {
        if (isRtl) {
          headers = showCompleted
            ? [
                'اسم القسم',
                'عدد الشيفات',
                'عدد المنتجات',
                'المطلوب',
                'المكتمل',
                'نسبة الإنجاز'
              ]
            : [
                'اسم القسم',
                'عدد الشيفات',
                'عدد المنتجات',
                'المطلوب'
              ];
        } else {
          headers = showCompleted
            ? [
                'Department Name',
                'Chefs Count',
                'Products Count',
                'Assigned',
                'Completed',
                'Completion %'
              ]
            : [
                'Department Name',
                'Chefs Count',
                'Products Count',
                'Assigned'
              ];
        }

        body = displayedDepartments.map(dept => {
          const departmentChefs = getChefsByDepartment(dept.name);
          const departmentProducts = getProductsByDepartment(dept.name);
          
          let totalAssigned = 0;
          let totalCompleted = 0;
          
          departmentChefs.forEach(chef => {
            const totals = calculateChefTotals(chef);
            totalAssigned += totals.assignedTotal;
            totalCompleted += totals.completedTotal;
          });
          
          const completionRate = totalAssigned > 0 ? (totalCompleted / totalAssigned) * 100 : 0;
          
          if (isRtl) {
            const row = [
              fixArabicText(dept.name),
              toArabicNumerals(departmentChefs.length.toString()),
              toArabicNumerals(departmentProducts.length.toString()),
              toArabicNumerals(formatNumberWithComma(totalAssigned))
            ];
            
            if (showCompleted) {
              row.push(toArabicNumerals(formatNumberWithComma(totalCompleted)));
              row.push(toArabicNumerals(formatNumberWithComma(completionRate)) + '%');
            }
            
            return row;
          } else {
            const row = [
              dept.name,
              departmentChefs.length.toString(),
              departmentProducts.length.toString(),
              formatNumberWithComma(totalAssigned)
            ];
            
            if (showCompleted) {
              row.push(formatNumberWithComma(totalCompleted));
              row.push(formatNumberWithComma(completionRate) + '%');
            }
            
            return row;
          }
        });
        
        const totalAssignedAll = displayedDepartments.reduce((sum, dept) => {
          const departmentChefs = getChefsByDepartment(dept.name);
          return sum + departmentChefs.reduce((s, chef) => s + calculateChefTotals(chef).assignedTotal, 0);
        }, 0);
        
        const totalCompletedAll = displayedDepartments.reduce((sum, dept) => {
          const departmentChefs = getChefsByDepartment(dept.name);
          return sum + departmentChefs.reduce((s, chef) => s + calculateChefTotals(chef).completedTotal, 0);
        }, 0);
        
        const totalCompletionRate = totalAssignedAll > 0 ? (totalCompletedAll / totalAssignedAll) * 100 : 0;
        const totalChefs = new Set(displayedDepartments.flatMap(dept => 
          getChefsByDepartment(dept.name).map(c => c.id)
        )).size;
        
        const totalProducts = new Set(displayedDepartments.flatMap(dept => 
          getProductsByDepartment(dept.name).map(p => p.id)
        )).size;
        
        if (isRtl) {
          const totalRow = [
            'الإجمالي',
            toArabicNumerals(totalChefs.toString()),
            toArabicNumerals(totalProducts.toString()),
            toArabicNumerals(formatNumberWithComma(totalAssignedAll))
          ];
          
          if (showCompleted) {
            totalRow.push(toArabicNumerals(formatNumberWithComma(totalCompletedAll)));
            totalRow.push(toArabicNumerals(formatNumberWithComma(totalCompletionRate)) + '%');
          }
          
          body.push(totalRow);
        } else {
          const totalRow = [
            'Total',
            totalChefs.toString(),
            totalProducts.toString(),
            formatNumberWithComma(totalAssignedAll)
          ];
          
          if (showCompleted) {
            totalRow.push(formatNumberWithComma(totalCompletedAll));
            totalRow.push(formatNumberWithComma(totalCompletionRate) + '%');
          }
          
          body.push(totalRow);
        }
      }

      const columnCount = headers.length;
      const availableWidth = pageWidth - (margin * 2);
      
      const columnStyles: any = {};
      const chefCount = viewType === 'chefs' ? chefsData.length : 0;
      
      if (viewType === 'chefs') {
        columnStyles[0] = { cellWidth: 15, halign: 'center', valign: 'middle' };
        columnStyles[1] = { cellWidth: 40, halign: isRtl ? 'right' : 'left', valign: 'middle' };
        columnStyles[2] = { cellWidth: 25, halign: 'center', valign: 'middle' };
        
        const chefColWidth = Math.max(15, Math.floor((availableWidth - 80) / (chefCount + 1)));
        for (let i = 3; i < columnCount - 1; i++) {
          columnStyles[i] = { 
            cellWidth: chefColWidth, 
            halign: 'center', 
            valign: 'middle' 
          };
        }
        
        columnStyles[columnCount - 1] = { 
          cellWidth: chefColWidth, 
          halign: 'center', 
          valign: 'middle',
          fontStyle: 'bold',
          fillColor: [254, 243, 199]
        };
      } else {
        const deptColWidth = availableWidth / columnCount;
        for (let i = 0; i < columnCount; i++) {
          columnStyles[i] = { 
            cellWidth: deptColWidth,
            halign: i === 0 ? (isRtl ? 'right' : 'left') : 'center',
            valign: 'middle'
          };
        }
      }

      autoTable(doc, {
        head: [headers],
        body: body,
        startY: yPos,
        margin: { left: margin, right: margin },
        theme: 'grid',
        styles: {
          font: fontName,
          fontSize: viewType === 'chefs' ? 7 : 8,
          textColor: textColor,
          lineColor: borderColor,
          lineWidth: 0.1,
          halign: isRtl ? 'right' : 'left',
          valign: 'middle',
          cellPadding: viewType === 'chefs' ? 2 : 3,
          minCellHeight: viewType === 'chefs' ? 6 : 8,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: headerBgColor,
          textColor: 255,
          fontSize: viewType === 'chefs' ? 8 : 9,
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          cellPadding: viewType === 'chefs' ? 3 : 4,
          lineWidth: 0.1,
          overflow: 'linebreak',
          lineColor: [217, 119, 6],
          minCellHeight: viewType === 'chefs' ? 8 : 10
        },
        bodyStyles: {
          fontSize: viewType === 'chefs' ? 7 : 8,
          halign: isRtl ? 'right' : 'left',
          valign: 'middle',
          cellPadding: viewType === 'chefs' ? 2 : 3,
          lineWidth: 0.1,
          minCellHeight: viewType === 'chefs' ? 6 : 8,
          overflow: 'linebreak'
        },
        alternateRowStyles: { 
          fillColor: altRowColor 
        },
        columnStyles: columnStyles,
        didParseCell: (hookData: any) => {
          if (hookData.section === 'body' && hookData.row.index === hookData.table.body.length - 1) {
            hookData.cell.styles.fontStyle = 'bold';
            hookData.cell.styles.fillColor = totalRowColor;
            hookData.cell.styles.textColor = [255, 255, 255];
            hookData.cell.styles.fontSize = viewType === 'chefs' ? 8 : 9;
            hookData.cell.styles.minCellHeight = viewType === 'chefs' ? 7 : 9;
            hookData.cell.styles.halign = 'center';
          }
          
          if (viewType === 'chefs' && showCompleted && hookData.row.index === 0) {
            hookData.cell.styles.fillColor = [16, 185, 129];
            hookData.cell.styles.textColor = [255, 255, 255];
            hookData.cell.styles.fontStyle = 'bold';
            hookData.cell.styles.fontSize = 8;
          }
          
          if (viewType === 'chefs' && showCompleted && hookData.row.index > 0 && hookData.row.index <= allProducts.length) {
            hookData.cell.styles.fillColor = [209, 250, 229];
            hookData.cell.styles.textColor = [6, 95, 70];
            hookData.cell.styles.fontStyle = 'bold';
          }
          
          if (isRtl && hookData.section === 'body' && typeof hookData.cell.text === 'string') {
            hookData.cell.text = hookData.cell.text.replace(/\d+/g, (match: string) => 
              toArabicNumerals(match)
            );
          }
          
          if (typeof hookData.cell.text === 'string') {
            if (hookData.cell.text.includes('%') || 
                (/^\d+$/.test(hookData.cell.text.replace(/,/g, '')))) {
              hookData.cell.styles.halign = 'center';
            }
          }
        }
      });
    }

    const finalY = (doc as any).lastAutoTable.finalY || yPos + 100;
    doc.setFontSize(7);
    doc.setTextColor(...lightTextColor);
    const note = isRtl 
      ? '* تم إنشاء هذا التقرير بواسطة نظام إدارة الجوديا'
      : '* This report was generated by aljodia Management System';
    doc.text(note, pageWidth / 2, finalY + 10, { align: 'center' });

    const fileName = generateFileName(
      viewType,
      periodLabel,
      selectedChefsMulti,
      selectedDepartmentsMulti,
      searchQuery,
      sortBy,
      showCompleted,
      isRtl,
      'pdf'
    );

    doc.save(fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى PDF بنجاح!' : 'Exported to PDF successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('PDF Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير PDF. حاول مرة أخرى.' : 'PDF export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === EXCEL EXPORT - FIXED ===
// ==============================
const exportToExcel = async (
  chefsData: ChefProductionData[],
  allProducts: ProductData[],
  displayedDepartments: any[],
  viewType: 'chefs' | 'departments',
  periodLabel: string,
  sortBy: string,
  selectedChefsMulti: string[],
  selectedDepartmentsMulti: string[],
  searchQuery: string,
  isRtl: boolean,
  language: string,
  showCompleted: boolean,
  
  calculateProductTotals: (product: ProductData) => { assignedTotal: number, completedTotal: number },
  calculateChefTotals: (chef: ChefProductionData) => { assignedTotal: number, completedTotal: number },
  getProductValuesForChef: (chef: ChefProductionData, product: ProductData) => { assigned: number, completed: number },
  getChefsByDepartment: (departmentName: string) => ChefProductionData[],
  getProductsByDepartment: (departmentName: string) => ProductData[]
) => {
  const toastId = 'excel-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف Excel...' : 'Generating Excel...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const workbook = new ExcelJS.Workbook();
    
    const sheetName = viewType === 'chefs' 
      ? (isRtl ? 'الشيفات' : 'Chefs') 
      : (isRtl ? 'الأقسام' : 'Departments');
    
    const worksheet = workbook.addWorksheet(sheetName, {
      views: [{ 
        rightToLeft: isRtl,
        showGridLines: true 
      }]
    });

    const title = viewType === 'chefs'
      ? (isRtl ? 'تقرير إنتاج الشيفات' : 'Chefs Production Report')
      : (isRtl ? 'تقرير إنتاج الأقسام' : 'Departments Production Report');
    
    const titleRow = worksheet.addRow([title]);
    titleRow.height = 28;
    const titleCell = titleRow.getCell(1);
    titleCell.font = { 
      size: 16, 
      bold: true, 
      color: { argb: '1F2937' },
      name: isRtl ? 'Arial' : 'Calibri'
    };
    titleCell.alignment = { 
      horizontal: 'center', 
      vertical: 'middle',
      wrapText: true 
    };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FEF3C7' }
    };

    const filterInfoParts = [
      periodLabel,
      viewType === 'chefs' 
        ? (isRtl ? 'عرض: حسب الشيفات' : 'View: By Chefs')
        : (isRtl ? 'عرض: حسب الأقسام' : 'View: By Departments')
    ];
    
    if (!showCompleted) {
      filterInfoParts.push(isRtl ? 'بدون المكتمل' : 'Without Completed');
    }
    
    const filterInfoText = filterInfoParts.join('  |  ');
    const infoRow = worksheet.addRow([filterInfoText]);
    const infoCell = infoRow.getCell(1);
    infoCell.font = { 
      size: 10, 
      color: { argb: '6B7280' },
      name: isRtl ? 'Arial' : 'Calibri'
    };
    infoCell.alignment = { 
      horizontal: 'center', 
      vertical: 'middle',
      wrapText: true 
    };
    infoCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'F9FAFB' }
    };

    worksheet.addRow([]);

    if (viewType === 'chefs') {
      const totalColumns = 4 + chefsData.length;
      
      worksheet.mergeCells(`A1:${String.fromCharCode(65 + totalColumns - 1)}1`);
      worksheet.mergeCells(`A2:${String.fromCharCode(65 + totalColumns - 1)}2`);
      
      const headers = [
        isRtl ? 'كود المنتج' : 'Product Code',
        isRtl ? 'اسم المنتج' : 'Product Name',
        isRtl ? 'القسم' : 'Department',
        ...chefsData.map(chef => fixArabicText(chef.name)),
        isRtl ? 'الإجمالي' : 'Total'
      ];
      
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 25;
      
      headerRow.eachCell((cell, colNumber) => {
        cell.font = { 
          bold: true, 
          size: 11, 
          color: { argb: 'FFFFFF' },
          name: isRtl ? 'Arial' : 'Calibri'
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F59E0B' }
        };
        cell.alignment = { 
          horizontal: 'center', 
          vertical: 'middle',
          wrapText: true 
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'D97706' } },
          left: { style: 'thin', color: { argb: 'D97706' } },
          bottom: { style: 'thin', color: { argb: 'D97706' } },
          right: { style: 'thin', color: { argb: 'D97706' } }
        };
      });
      
      allProducts.forEach((product, productIndex) => {
        const rowData = [
          product.code || '-',
          fixArabicText(product.name),
          fixArabicText(product.department || '-'),
          ...chefsData.map(chef => {
            const values = getProductValuesForChef(chef, product);
            return formatNumberWithComma(values.assigned);
          }),
          formatNumberWithComma(calculateProductTotals(product).assignedTotal)
        ];
        
        const row = worksheet.addRow(rowData);
        row.height = 20;
        
        row.eachCell((cell, colNumber) => {
          cell.alignment = { 
            horizontal: colNumber <= 3 ? (isRtl ? 'right' : 'left') : 'center', 
            vertical: 'middle',
            wrapText: true 
          };
          cell.border = {
            top: { style: 'thin', color: { argb: 'E5E7EB' } },
            left: { style: 'thin', color: { argb: 'E5E7EB' } },
            bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
            right: { style: 'thin', color: { argb: 'E5E7EB' } }
          };
          
          if (productIndex % 2 === 0) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'F9FAFB' }
            };
          }
          
          if (colNumber >= 4) {
            cell.font = { 
              size: 10,
              name: isRtl ? 'Arial' : 'Calibri'
            };
          }
        });
      });
      
      const totalRowData = [
        isRtl ? 'الإجمالي' : 'Total',
        '',
        '',
        ...chefsData.map(chef => {
          const totals = calculateChefTotals(chef);
          return formatNumberWithComma(totals.assignedTotal);
        }),
        formatNumberWithComma(chefsData.reduce((sum, chef) => sum + calculateChefTotals(chef).assignedTotal, 0))
      ];
      
      const totalRow = worksheet.addRow(totalRowData);
      totalRow.height = 25;
      
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { 
          bold: true, 
          size: 11, 
          color: { argb: 'FFFFFF' },
          name: isRtl ? 'Arial' : 'Calibri'
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F59E0B' }
        };
        cell.alignment = { 
          horizontal: 'center', 
          vertical: 'middle' 
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'D97706' } },
          left: { style: 'thin', color: { argb: 'D97706' } },
          bottom: { style: 'thin', color: { argb: 'D97706' } },
          right: { style: 'thin', color: { argb: 'D97706' } }
        };
      });
      
      worksheet.columns = [
        { width: 15 },
        { width: 30 },
        { width: 20 },
        ...chefsData.map(() => ({ width: 20 })),
        { width: 25 }
      ];
      
    } else {
      const totalColumns = showCompleted ? 6 : 4;
      worksheet.mergeCells(`A1:${String.fromCharCode(65 + totalColumns - 1)}1`);
      worksheet.mergeCells(`A2:${String.fromCharCode(65 + totalColumns - 1)}2`);
      
      let headers: string[];
      if (isRtl) {
        headers = [
          'اسم القسم',
          'عدد الشيفات',
          'عدد المنتجات',
          'المطلوب'
        ];
        if (showCompleted) {
          headers.push('المكتمل');
          headers.push('نسبة الإنجاز %');
        }
      } else {
        headers = [
          'Department Name',
          'Chefs Count',
          'Products Count',
          'Assigned'
        ];
        if (showCompleted) {
          headers.push('Completed');
          headers.push('Completion %');
        }
      }
      
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 25;
      
      headerRow.eachCell((cell, colNumber) => {
        cell.font = { 
          bold: true, 
          size: 11, 
          color: { argb: 'FFFFFF' },
          name: isRtl ? 'Arial' : 'Calibri'
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '3B82F6' }
        };
        cell.alignment = { 
          horizontal: 'center', 
          vertical: 'middle',
          wrapText: true 
        };
        cell.border = {
          top: { style: 'thin', color: { argb: '1D4ED8' } },
          left: { style: 'thin', color: { argb: '1D4ED8' } },
          bottom: { style: 'thin', color: { argb: '1D4ED8' } },
          right: { style: 'thin', color: { argb: '1D4ED8' } }
        };
      });
      
      displayedDepartments.forEach((dept, deptIndex) => {
        const departmentChefs = getChefsByDepartment(dept.name);
        const departmentProducts = getProductsByDepartment(dept.name);
        
        let totalAssigned = 0;
        let totalCompleted = 0;
        
        departmentChefs.forEach(chef => {
          const totals = calculateChefTotals(chef);
          totalAssigned += totals.assignedTotal;
          totalCompleted += totals.completedTotal;
        });
        
        const completionRate = totalAssigned > 0 ? (totalCompleted / totalAssigned) * 100 : 0;
        
        const rowData = [
          fixArabicText(dept.name),
          departmentChefs.length,
          departmentProducts.length,
          totalAssigned
        ];
        
        if (showCompleted) {
          rowData.push(totalCompleted);
          rowData.push(completionRate / 100);
        }
        
        const row = worksheet.addRow(rowData);
        row.height = 20;
        
        row.eachCell((cell, colNumber) => {
          cell.alignment = { 
            horizontal: colNumber === 1 ? (isRtl ? 'right' : 'left') : 'center', 
            vertical: 'middle',
            wrapText: true 
          };
          cell.border = {
            top: { style: 'thin', color: { argb: 'E5E7EB' } },
            left: { style: 'thin', color: { argb: 'E5E7EB' } },
            bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
            right: { style: 'thin', color: { argb: 'E5E7EB' } }
          };
          
          if (deptIndex % 2 === 0) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'F9FAFB' }
            };
          }
          
          if (colNumber >= 2) {
            if (colNumber === rowData.length && showCompleted) {
              cell.numFmt = '0.00%';
              cell.font = { color: { argb: '2563EB' } };
              
              const rate = cell.value as number;
              if (rate >= 0.9) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D1FAE5' } };
              } else if (rate >= 0.7) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
              } else if (rate > 0) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } };
              }
            } else if (colNumber === rowData.length - 1 && showCompleted) {
              cell.numFmt = '#,##0';
              cell.font = { color: { argb: '059669' } };
            } else if (colNumber === rowData.length - (showCompleted ? 2 : 1)) {
              cell.numFmt = '#,##0';
              cell.font = { color: { argb: 'D97706' } };
            } else if (colNumber === 2 || colNumber === 3) {
              cell.numFmt = '#,##0';
            }
          }
        });
      });
      
      const totalAssignedAll = displayedDepartments.reduce((sum, dept) => {
        const departmentChefs = getChefsByDepartment(dept.name);
        return sum + departmentChefs.reduce((s, chef) => s + calculateChefTotals(chef).assignedTotal, 0);
      }, 0);
      
      const totalCompletedAll = displayedDepartments.reduce((sum, dept) => {
        const departmentChefs = getChefsByDepartment(dept.name);
        return sum + departmentChefs.reduce((s, chef) => s + calculateChefTotals(chef).completedTotal, 0);
      }, 0);
      
      const totalCompletionRate = totalAssignedAll > 0 ? (totalCompletedAll / totalAssignedAll) * 100 : 0;
      const totalChefs = new Set(displayedDepartments.flatMap(dept => 
        getChefsByDepartment(dept.name).map(c => c.id)
      )).size;
      
      const totalProducts = new Set(displayedDepartments.flatMap(dept => 
        getProductsByDepartment(dept.name).map(p => p.id)
      )).size;
      
      const totalRowData = [
        isRtl ? 'الإجمالي' : 'Total',
        totalChefs,
        totalProducts,
        totalAssignedAll
      ];
      
      if (showCompleted) {
        totalRowData.push(totalCompletedAll);
        totalRowData.push(totalCompletionRate / 100);
      }
      
      const totalRow = worksheet.addRow(totalRowData);
      totalRow.height = 25;
      
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { 
          bold: true, 
          size: 11, 
          color: { argb: 'FFFFFF' },
          name: isRtl ? 'Arial' : 'Calibri'
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '3B82F6' }
        };
        cell.alignment = { 
          horizontal: colNumber === 1 ? 'center' : 'center', 
          vertical: 'middle' 
        };
        cell.border = {
          top: { style: 'thin', color: { argb: '1D4ED8' } },
          left: { style: 'thin', color: { argb: '1D4ED8' } },
          bottom: { style: 'thin', color: { argb: '1D4ED8' } },
          right: { style: 'thin', color: { argb: '1D4ED8' } }
        };
        
        if (colNumber >= 2) {
          if (colNumber === totalRowData.length && showCompleted) {
            cell.numFmt = '0.00%';
          } else if (colNumber >= 2 && colNumber <= totalRowData.length - (showCompleted ? 1 : 0)) {
            cell.numFmt = '#,##0';
          }
        }
      });
      
      if (showCompleted) {
        worksheet.columns = [
          { width: 25 },
          { width: 15 },
          { width: 15 },
          { width: 15 },
          { width: 15 },
          { width: 15 }
        ];
      } else {
        worksheet.columns = [
          { width: 25 },
          { width: 15 },
          { width: 15 },
          { width: 15 }
        ];
      }
    }

    const fileName = generateFileName(
      viewType,
      periodLabel,
      selectedChefsMulti,
      selectedDepartmentsMulti,
      searchQuery,
      sortBy,
      showCompleted,
      isRtl,
      'xlsx'
    );

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى Excel بنجاح!' : 'Exported to Excel successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Excel Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير Excel. حاول مرة أخرى.' : 'Excel export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === COMPONENTS ===
// ==============================

const MultiSelectDropdown: React.FC<{
  value: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  label?: string;
  isRtl: boolean;
  placeholder?: string;
}> = ({ value, onChange, options, label, isRtl, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleOption = useCallback((optionValue: string) => {
    if (optionValue === 'all') {
      onChange(['all']);
    } else {
      const newValues = value.filter(v => v !== 'all');
      if (newValues.includes(optionValue)) {
        const filtered = newValues.filter(v => v !== optionValue);
        onChange(filtered.length === 0 ? ['all'] : filtered);
      } else {
        onChange([...newValues, optionValue]);
      }
    }
  }, [value, onChange]);

  const allSelected = value.includes('all') || value.length === 0;
  const count = value.filter(v => v !== 'all').length;
  
  let displayLabel = '';
  if (allSelected) {
    displayLabel = isRtl ? 'الكل' : 'All';
  } else if (count === 0) {
    displayLabel = placeholder || (isRtl ? 'اختر...' : 'Select...');
  } else if (count === 1) {
    const selectedOption = options.find(opt => opt.value === value[0]);
    displayLabel = selectedOption?.label || (isRtl ? 'محدد' : 'Selected');
  } else {
    displayLabel = isRtl 
      ? `${count} ${count === 2 ? 'محددين' : 'محدد'}`
      : `${count} selected`;
  }

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          dir={isRtl ? 'rtl' : 'ltr'}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full py-2 px-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white shadow-sm text-sm font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
          type="button"
        >
          <span className="truncate flex-1">{displayLabel}</span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
          >
            {options.map(opt => {
              const isSelected = allSelected ? opt.value === 'all' : value.includes(opt.value);
              return (
                <div 
                  key={opt.value} 
                  className="flex items-center justify-between px-3 py-2 hover:bg-amber-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                  onClick={() => toggleOption(opt.value)}
                >
                  <span className="text-sm text-gray-700 flex-1" dir={isRtl ? 'rtl' : 'ltr'}>
                    {opt.label}
                  </span>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    className="h-4 w-4 text-amber-500 rounded border-gray-300 focus:ring-amber-300 cursor-pointer"
                    readOnly
                  />
                </div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ==============================
// === COMPACT EXPORT DROPDOWN ===
// ==============================
const CompactExportDropdown: React.FC<{
  onExportPDF: (showCompleted: boolean) => void;
  onExportExcel: (showCompleted: boolean) => void;
  disabled: boolean;
  isRtl: boolean;
  showCompleted: boolean;
  onToggleShowCompleted: () => void;
}> = ({ onExportPDF, onExportExcel, disabled, isRtl, showCompleted, onToggleShowCompleted }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onToggleShowCompleted}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
          showCompleted
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
            : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
        }`}
        type="button"
      >
        <CheckCircle className="w-4 h-4" />
        <span className="hidden sm:inline">
          {showCompleted ? (isRtl ? 'المكتمل' : 'Completed') : (isRtl ? 'بدون مكتمل' : 'Without')}
        </span>
      </button>
      
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className="flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          type="button"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">{isRtl ? 'تصدير' : 'Export'}</span>
        </button>
        
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 mt-1.5 ${isRtl ? 'left-0' : 'right-0'} w-36 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden`}
          >
            <button
              onClick={() => {
                onExportExcel(showCompleted);
                setIsOpen(false);
              }}
              disabled={disabled}
              className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors border-b border-gray-100 flex items-center gap-2"
              type="button"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span>Excel</span>
            </button>
            <button
              onClick={() => {
                onExportPDF(showCompleted);
                setIsOpen(false);
              }}
              disabled={disabled}
              className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors flex items-center gap-2"
              type="button"
            >
              <FileText className="w-4 h-4" />
              <span>PDF</span>
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const Button: React.FC<{
  variant: 'primary' | 'outline' | 'success' | 'amber';
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
  size?: 'sm' | 'md';
}> = ({ variant, onClick, disabled, children, icon, size = 'md' }) => {
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm'
  };
  
  const variantClasses = {
    amber: 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm hover:shadow',
    primary: 'bg-gray-800 hover:bg-gray-900 text-white shadow-sm hover:shadow',
    success: 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm hover:shadow',
    outline: 'border border-gray-200 hover:bg-gray-50 text-gray-700 hover:border-gray-300'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-2 rounded-lg font-medium transition-all ${sizeClasses[size]} ${variantClasses[variant]} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      type="button"
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="whitespace-nowrap">{children}</span>
    </button>
  );
};

const SearchInput: React.FC<{
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  isRtl: boolean;
}> = ({ value, onChange, placeholder, isRtl }) => {
  return (
    <div className="relative w-full">
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${isRtl ? 'pl-10 pr-3' : 'pr-10 pl-3'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white text-sm ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
        dir={isRtl ? 'rtl' : 'ltr'}
      />
      {value ? (
        <button
          onClick={() => onChange({ target: { value: '' } } as any)}
          className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors`}
          type="button"
        >
          <X className="w-4 h-4" />
        </button>
      ) : (
        <Search className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
      )}
    </div>
  );
};

const Dropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  label?: string;
  icon?: React.ReactNode;
  isRtl: boolean;
}> = ({ value, onChange, options, label, icon, isRtl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          dir={isRtl ? 'rtl' : 'ltr'}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full py-2 px-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white shadow-sm text-sm font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
          type="button"
        >
          <div className="flex items-center gap-2 flex-1 truncate" dir={isRtl ? 'rtl' : 'ltr'}>
            {icon && <span className="flex-shrink-0">{icon}</span>}
            <span className="truncate">{selectedOption?.label || ''}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
          >
            {options.map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-sm ${isRtl ? 'text-right' : 'text-left'} text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors border-b border-gray-100 last:border-b-0`}
                type="button"
                dir={isRtl ? 'rtl' : 'ltr'}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

const StatsCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'gray' | 'emerald' | 'blue' | 'amber' | 'green';
  loading?: boolean;
  isRtl: boolean;
}> = ({ title, value, icon, color, loading, isRtl }) => {
  const colors = {
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    green: 'bg-green-50 text-green-700 border-green-200'
  };

  return (
    <div className={`p-4 rounded-xl border ${colors[color]} transition-all hover:shadow-sm`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg bg-white">
          {icon}
        </div>
      </div>
      <div className="mb-2">
        <h3 className="text-sm font-medium opacity-80">{title}</h3>
      </div>
      <div className="text-xl font-bold">
        {loading ? (
          <div className="h-7 w-24 bg-gray-200 rounded animate-pulse"></div>
        ) : (
          value
        )}
      </div>
    </div>
  );
};

// ==============================
// === MAIN COMPONENT ===
// ==============================
const ProductionReport: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRtl = language === 'ar';
  const isChef = user?.role === 'chef';
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [chefsData, setChefsData] = useState<ChefProductionData[]>([]);
  const [chefsList, setChefsList] = useState<Chef[]>([]);
  const [expandedChefs, setExpandedChefs] = useState<number>(10);
  const [viewType, setViewType] = useState<'chefs' | 'departments'>('chefs');
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  const [departmentProducts, setDepartmentProducts] = useState<Map<string, ProductData[]>>(new Map());
  
  const [showCompleted, setShowCompleted] = useState(true);
  
  const [period, setPeriod] = useState('month');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('code_asc');
  const [selectedChefsMulti, setSelectedChefsMulti] = useState<string[]>(['all']);
  const [selectedDepartmentsMulti, setSelectedDepartmentsMulti] = useState<string[]>(['all']);
  
  const periodOptions = useMemo(() => [
    { value: 'today', label: isRtl ? 'اليوم' : 'Today' },
    { value: 'yesterday', label: isRtl ? 'أمس' : 'Yesterday' },
    { value: 'week', label: isRtl ? 'آخر 7 أيام' : 'Last 7 Days' },
    { value: 'month', label: isRtl ? 'آخر 30 يوم' : 'Last 30 Days' },
  ], [isRtl]);
  
  const viewTypeOptions = useMemo(() => [
    { value: 'chefs', label: isRtl ? 'الشيفات' : 'Chefs', icon: <ChefHat className="w-4 h-4" /> },
    { value: 'departments', label: isRtl ? 'الأقسام' : 'Departments', icon: <Building2 className="w-4 h-4" /> },
  ], [isRtl]);
  
  const sortOptions = useMemo(() => [
    { value: 'code_asc', label: isRtl ? 'الكود (أ-ي)' : 'Code (A-Z)' },
    { value: 'code_desc', label: isRtl ? 'الكود (ي-أ)' : 'Code (Z-A)' },
    { value: 'name_asc', label: isRtl ? 'اسم المنتج (أ-ي)' : 'Product Name (A-Z)' },
    { value: 'name_desc', label: isRtl ? 'اسم المنتج (ي-أ)' : 'Product Name (Z-A)' },
    { value: 'quantity_desc', label: isRtl ? 'الكمية (الأكثر أولاً)' : 'Quantity (High to Low)' },
    { value: 'department_asc', label: isRtl ? 'القسم (أ-ي)' : 'Department (A-Z)' },
  ], [isRtl]);

  const chefMultiSelectOptions = useMemo(() => {
    const baseOptions = chefsData.map(chef => ({
      value: chef.id,
      label: fixArabicText(chef.name)
    })).sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: 'all', label: isRtl ? 'جميع الشيفات' : 'All Chefs' }, ...baseOptions];
  }, [chefsData, isRtl]);

  const departmentMultiSelectOptions = useMemo(() => {
    const departmentsWithProducts = new Set<string>();
    
    chefsData.forEach(chef => {
      if (chef.products && typeof chef.products === 'object') {
        Object.values(chef.products).forEach((product: any) => {
          if (product.department && product.department !== 'غير محدد') {
            departmentsWithProducts.add(product.department);
          }
        });
      }
      
      chef.departments?.forEach(dept => {
        if (dept.name) departmentsWithProducts.add(dept.name);
      });
    });
    
    const uniqueDepartments = Array.from(departmentsWithProducts).filter(Boolean).sort();
    
    const baseOptions = uniqueDepartments.map(dept => ({
      value: dept,
      label: fixArabicText(dept)
    }));
    return [{ value: 'all', label: isRtl ? 'جميع الأقسام' : 'All Departments' }, ...baseOptions];
  }, [chefsData, isRtl]);

  const allProducts = useMemo(() => {
    const productMap = new Map<string, ProductData>();
    
    chefsData.forEach(chef => {
      if (chef.products && typeof chef.products === 'object') {
        Object.entries(chef.products).forEach(([productId, product]) => {
          if (product && typeof product === 'object' && product.id) {
            let productDepartment = product.department;
            
            if (!productDepartment || productDepartment === 'غير محدد') {
              const chefDepartments = chef.departments || [];
              if (chefDepartments.length > 0) {
                productDepartment = chefDepartments[0].name;
              }
            }
            
            const enhancedProduct = {
              ...product,
              department: productDepartment || 'غير محدد',
            };
            
            productMap.set(product.id, enhancedProduct);
          }
        });
      }
    });
    
    const productsArray = Array.from(productMap.values());
    
    return productsArray.sort((a, b) => {
      switch (sortBy) {
        case 'code_asc':
          return (a.code || '').localeCompare(b.code || '');
        case 'code_desc':
          return (b.code || '').localeCompare(a.code || '');
        case 'name_asc':
          return fixArabicText(a.name).localeCompare(fixArabicText(b.name));
        case 'name_desc':
          return fixArabicText(b.name).localeCompare(fixArabicText(a.name));
        case 'quantity_desc':
          const totalA = chefsData.reduce((sum, chef) => sum + (chef.products?.[a.id]?.totalAssigned || 0), 0);
          const totalB = chefsData.reduce((sum, chef) => sum + (chef.products?.[b.id]?.totalAssigned || 0), 0);
          return totalB - totalA;
        case 'department_asc':
          const deptA = a.department || 'غير محدد';
          const deptB = b.department || 'غير محدد';
          return fixArabicText(deptA).localeCompare(fixArabicText(deptB));
        default:
          return fixArabicText(a.name).localeCompare(fixArabicText(b.name));
      }
    });
  }, [chefsData, sortBy]);

  const allDepartments = useMemo(() => {
    const departmentsMap = new Map<string, {
      name: string;
      products: ProductData[];
      chefs: ChefProductionData[];
      hasProducts: boolean;
    }>();
    
    allProducts.forEach(product => {
      if (product.department && product.department !== 'غير محدد') {
        if (!departmentsMap.has(product.department)) {
          departmentsMap.set(product.department, {
            name: product.department,
            products: [],
            chefs: [],
            hasProducts: false
          });
        }
        const dept = departmentsMap.get(product.department)!;
        if (!dept.products.some(p => p.id === product.id)) {
          dept.products.push(product);
          dept.hasProducts = true;
        }
      }
    });
    
    chefsData.forEach(chef => {
      chef.departments?.forEach(dept => {
        if (!departmentsMap.has(dept.name)) {
          departmentsMap.set(dept.name, {
            name: dept.name,
            products: [],
            chefs: [],
            hasProducts: false
          });
        }
        const deptData = departmentsMap.get(dept.name)!;
        if (!deptData.chefs.some(c => c.id === chef.id)) {
          deptData.chefs.push(chef);
        }
      });
    });
    
    return Array.from(departmentsMap.values())
      .filter(dept => dept.hasProducts || dept.products.length > 0)
      .sort((a, b) => fixArabicText(a.name).localeCompare(fixArabicText(b.name)));
  }, [allProducts, chefsData]);

  useEffect(() => {
    const newMap = new Map<string, ProductData[]>();
    
    allDepartments.forEach(dept => {
      newMap.set(dept.name, dept.products);
    });
    
    setDepartmentProducts(newMap);
  }, [allDepartments]);

  const getChefsByDepartment = useCallback((departmentName: string) => {
    return chefsData.filter(chef => {
      const chefDepts = chef.departments?.map(d => d.name) || [];
      return chefDepts.includes(departmentName);
    });
  }, [chefsData]);

  const getProductsByDepartment = useCallback((departmentName: string) => {
    return departmentProducts.get(departmentName) || [];
  }, [departmentProducts]);

  const filteredChefsData = useMemo(() => {
    let filtered = chefsData;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(chef => 
        chef.name?.toLowerCase().includes(query) ||
        chef.email?.toLowerCase().includes(query)
      );
    }
    
    if (viewType === 'chefs' && selectedChefsMulti.length > 0 && !selectedChefsMulti.includes('all')) {
      filtered = filtered.filter(chef => selectedChefsMulti.includes(chef.id));
    }
    
    if (viewType === 'departments' && selectedDepartmentsMulti.length > 0 && !selectedDepartmentsMulti.includes('all')) {
      filtered = filtered.filter(chef => {
        const chefDepts = chef.departments?.map(d => d.name) || [];
        return chefDepts.some(dept => selectedDepartmentsMulti.includes(dept));
      });
    }
    
    filtered = [...filtered].sort((a, b) => 
      fixArabicText(a.name || '').localeCompare(fixArabicText(b.name || ''))
    );
    
    return filtered;
  }, [chefsData, searchQuery, selectedChefsMulti, selectedDepartmentsMulti, viewType]);

  const displayedDepartments = useMemo(() => {
    let filtered = allDepartments;
    
    if (selectedDepartmentsMulti.length > 0 && !selectedDepartmentsMulti.includes('all')) {
      filtered = filtered.filter(dept => selectedDepartmentsMulti.includes(dept.name));
    }
    
    return filtered;
  }, [allDepartments, selectedDepartmentsMulti]);

  const displayedChefs = useMemo(() => {
    return filteredChefsData.slice(0, expandedChefs);
  }, [filteredChefsData, expandedChefs]);

  const getProductValuesForChef = useCallback((chef: ChefProductionData, product: ProductData) => {
    if (!chef.products || !chef.products[product.id]) {
      return { assigned: 0, completed: 0 };
    }
    
    const chefProduct = chef.products[product.id];
    return {
      assigned: chefProduct.totalAssigned || 0,
      completed: chefProduct.totalCompleted || 0
    };
  }, []);

  const calculateChefTotals = useCallback((chef: ChefProductionData) => {
    let assignedTotal = 0;
    let completedTotal = 0;
    
    allProducts.forEach(product => {
      const values = getProductValuesForChef(chef, product);
      assignedTotal += values.assigned;
      completedTotal += values.completed;
    });
    
    return { assignedTotal, completedTotal };
  }, [allProducts, getProductValuesForChef]);

  const calculateProductTotals = useCallback((product: ProductData) => {
    let assignedTotal = 0;
    let completedTotal = 0;
    
    displayedChefs.forEach(chef => {
      const values = getProductValuesForChef(chef, product);
      assignedTotal += values.assigned;
      completedTotal += values.completed;
    });
    
    return { assignedTotal, completedTotal };
  }, [displayedChefs, getProductValuesForChef]);

  const calculateDepartmentTotals = useCallback((departmentName: string) => {
    const departmentChefs = getChefsByDepartment(departmentName);
    let assignedTotal = 0;
    let completedTotal = 0;
    
    departmentChefs.forEach(chef => {
      const totals = calculateChefTotals(chef);
      assignedTotal += totals.assignedTotal;
      completedTotal += totals.completedTotal;
    });
    
    return { assignedTotal, completedTotal };
  }, [getChefsByDepartment, calculateChefTotals]);

  const getDateRange = useCallback(() => {
    const { start, end } = calculatePeriodDates(period);
    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd')
    };
  }, [period]);

  const fetchChefsList = useCallback(async () => {
    try {
      const response = await chefsAPI.getAll();
      setChefsList(response);
    } catch (error) {
      toast.error(isRtl ? 'فشل في تحميل قائمة الشيفات' : 'Failed to load chefs list', {
        position: isRtl ? 'top-left' : 'top-right'
      });
    }
  }, [isRtl]);

  const fetchProductionData = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) setLoading(true);
    else setRefreshing(true);
    
    try {
      const range = getDateRange();
      const params: any = {
        startDate: range.startDate,
        endDate: range.endDate,
        groupBy: 'chef',
        lang: language,
        isRtl: isRtl ? 'true' : 'false'
      };
      
      const response = await reportsAPI.getChefsProductionSummary(params);
      
      let apiData: ChefProductionData[] = [];
      
      if (response.data?.success) {
        apiData = response.data.data || [];
      } else if (Array.isArray(response.data)) {
        apiData = response.data;
      } else if (Array.isArray(response)) {
        apiData = response;
      }
      
      setChefsData(apiData);
      
    } catch (error: any) {
      console.error('Error fetching production data:', error);
      toast.error(error.message || (isRtl ? 'فشل في تحميل بيانات الإنتاج' : 'Failed to load production data'), {
        position: isRtl ? 'top-left' : 'top-right'
      });
      setChefsData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getDateRange, isRtl, language]);

  useEffect(() => {
    let mounted = true;
    
    const initialize = async () => {
      try {
        await fetchChefsList();
        if (mounted) {
          await fetchProductionData();
        }
      } catch (error) {
        console.error('Initialization error:', error);
      }
    };
    
    initialize();
    
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProductionData();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [period, fetchProductionData]);

  const toggleExpandChefs = useCallback(() => {
    setExpandedChefs(expandedChefs === filteredChefsData.length ? 10 : filteredChefsData.length);
  }, [expandedChefs, filteredChefsData.length]);

  const toggleDepartment = useCallback((departmentName: string) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(departmentName)) {
        newSet.delete(departmentName);
      } else {
        newSet.add(departmentName);
      }
      return newSet;
    });
  }, []);

  const periodLabel = useMemo(() => {
    const locale = isRtl ? arSA : enUS;
    
    const { start, end } = calculatePeriodDates(period);
    if (isSameDay(start, end)) {
      return isRtl
        ? `${format(start, 'EEEE d/M/yyyy', { locale })}`
        : `${format(start, 'EEEE d/M/yyyy', { locale })}`;
    }
    return isRtl
      ? `${format(start, 'd/M/yyyy', { locale })} - ${format(end, 'd/M/yyyy', { locale })}`
      : `${format(start, 'd/M/yyyy', { locale })} - ${format(end, 'd/M/yyyy', { locale })}`;
  }, [period, isRtl]);
  
  const handleExportPDF = useCallback((showCompletedInExport: boolean = showCompleted) => {
    exportToPDF(
      filteredChefsData,
      allProducts,
      displayedDepartments,
      viewType,
      periodLabel,
      sortBy,
      selectedChefsMulti,
      selectedDepartmentsMulti,
      searchQuery,
      isRtl,
      language,
      showCompletedInExport,
      
      calculateProductTotals,
      calculateChefTotals,
      getProductValuesForChef,
      getChefsByDepartment,
      getProductsByDepartment
    );
  }, [filteredChefsData, allProducts, displayedDepartments, viewType, periodLabel, sortBy, 
      selectedChefsMulti, selectedDepartmentsMulti, searchQuery, isRtl, language, showCompleted,
      calculateProductTotals, calculateChefTotals, getProductValuesForChef, 
      getChefsByDepartment, getProductsByDepartment]);
  
  const handleExportExcel = useCallback((showCompletedInExport: boolean = showCompleted) => {
    exportToExcel(
      filteredChefsData,
      allProducts,
      displayedDepartments,
      viewType,
      periodLabel,
      sortBy,
      selectedChefsMulti,
      selectedDepartmentsMulti,
      searchQuery,
      isRtl,
      language,
      showCompletedInExport,
      
      calculateProductTotals,
      calculateChefTotals,
      getProductValuesForChef,
      getChefsByDepartment,
      getProductsByDepartment
    );
  }, [filteredChefsData, allProducts, displayedDepartments, viewType, periodLabel, sortBy,
      selectedChefsMulti, selectedDepartmentsMulti, searchQuery, isRtl, language, showCompleted,
      calculateProductTotals, calculateChefTotals, getProductValuesForChef,
      getChefsByDepartment, getProductsByDepartment]);
  
  const handleRefresh = useCallback(() => {
    fetchProductionData(true);
    toast.success(isRtl ? 'تم تحديث البيانات' : 'Data refreshed successfully', {
      position: isRtl ? 'top-left' : 'top-right'
    });
  }, [fetchProductionData, isRtl]);

  const toggleShowCompleted = useCallback(() => {
    setShowCompleted(prev => !prev);
  }, []);

  const renderChefsTable = () => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200" dir={isRtl ? 'rtl' : 'ltr'}>
          <thead>
            <tr className="bg-amber-50">
              <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[90px] sticky left-0 bg-amber-50 z-10">
                {isRtl ? 'الكود' : 'Code'}
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[200px] sticky left-[90px] bg-amber-50 z-10">
                {isRtl ? 'المنتج' : 'Product'}
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[120px]">
                {isRtl ? 'القسم' : 'Dept'}
              </th>
              
              {displayedChefs.map((chef, chefIndex) => (
                <th 
                  key={chef.id} 
                  colSpan={showCompleted ? 2 : 1}
                  className="px-0 py-3 text-center border-l border-amber-200 min-w-[140px]"
                >
                  <div className="flex flex-col items-center justify-center">
                    <div className="flex items-center gap-1 px-2">
                      <ChefHat className="w-3 h-3 text-amber-600" />
                      <span className="text-xs font-medium truncate" title={chef.name}>
                        {fixArabicText(chef.name)}
                      </span>
                    </div>
                    <div className="flex w-full border-t border-amber-200 mt-1">
                      <div className={`text-center py-1 text-[10px] font-medium bg-amber-100 text-amber-800 ${showCompleted ? 'w-1/2' : 'w-full'}`}>
                        {isRtl ? 'مطلوب' : 'A'}
                      </div>
                      {showCompleted && (
                        <div className="w-1/2 text-center py-1 text-[10px] font-medium bg-emerald-100 text-emerald-800">
                          {isRtl ? 'مكتمل' : 'C'}
                        </div>
                      )}
                    </div>
                  </div>
                </th>
              ))}
              
              {filteredChefsData.length > 10 && (
                <th className="px-3 py-3 text-center border-l border-amber-200">
                  <button
                    onClick={toggleExpandChefs}
                    className="flex items-center justify-center gap-1 text-amber-600 hover:text-amber-700 text-xs font-medium"
                  >
                    {expandedChefs === filteredChefsData.length ? (
                      <>
                        <ChevronUp className="w-3 h-3" />
                        {isRtl ? 'إخفاء' : 'Less'}
                      </>
                    ) : (
                      <>
                        <ChevronRight className="w-3 h-3" />
                        +{filteredChefsData.length - 10}
                      </>
                    )}
                  </button>
                </th>
              )}
              
              <th colSpan={showCompleted ? 2 : 1} className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap min-w-[140px] bg-amber-100 sticky right-0 z-10">
                <div className="flex flex-col">
                  <span>{isRtl ? 'الإجمالي' : 'Total'}</span>
                  <div className="flex w-full border-t border-amber-200 mt-1">
                    <div className={`text-center py-1 text-[10px] font-medium bg-amber-100 text-amber-800 ${showCompleted ? 'w-1/2' : 'w-full'}`}>
                      {isRtl ? 'مطلوب' : 'A'}
                    </div>
                    {showCompleted && (
                      <div className="w-1/2 text-center py-1 text-[10px] font-medium bg-emerald-100 text-emerald-800">
                        {isRtl ? 'مكتمل' : 'C'}
                      </div>
                    )}
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {allProducts.map((product, productIndex) => {
              const productTotals = calculateProductTotals(product);
              
              return (
                <tr
                  key={product.id}
                  className={`transition-colors ${productIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'} hover:bg-gray-50`}
                >
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200 sticky left-0 bg-white">
                    <span className="font-mono bg-gray-100 px-3 py-1.5 rounded text-xs font-medium text-gray-700">
                      {product.code || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200 sticky left-[90px] bg-white">
                    <span className="font-medium text-gray-900 text-sm">
                      {fixArabicText(product.name)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200">
                    <span className="text-xs text-gray-600">
                      {fixArabicText(product.department || '-')}
                    </span>
                  </td>
                  
                  {displayedChefs.map((chef) => {
                    const values = getProductValuesForChef(chef, product);
                    
                    return (
                      <React.Fragment key={`${product.id}-${chef.id}`}>
                        <td className={`px-3 py-3 text-center border-l border-gray-200 ${getQuantityBgColor(values.assigned, false)}`}>
                          <span className={`font-medium text-sm ${getQuantityColor(values.assigned, false)}`}>
                            {values.assigned === 0 ? '-' : formatNumber(values.assigned, isRtl)}
                          </span>
                        </td>
                        {showCompleted && (
                          <td className={`px-3 py-3 text-center border-r border-gray-200 ${getQuantityBgColor(values.completed, true)}`}>
                            <span className={`font-medium text-sm ${getQuantityColor(values.completed, true)}`}>
                              {values.completed === 0 ? '-' : formatNumber(values.completed, isRtl)}
                            </span>
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                  
                  {filteredChefsData.length > 10 && (
                    <td className="px-3 py-3 text-center border-l border-gray-200 bg-gray-50"></td>
                  )}
                  
                  <td className={`px-4 py-3 text-center border-l border-gray-200 ${getQuantityBgColor(productTotals.assignedTotal, false)} sticky ${showCompleted ? 'right-[70px]' : 'right-0'} bg-amber-50`}>
                    <span className={`font-bold text-sm ${getQuantityColor(productTotals.assignedTotal, false)}`}>
                      {formatNumber(productTotals.assignedTotal, isRtl)}
                    </span>
                  </td>
                  {showCompleted && (
                    <td className={`px-4 py-3 text-center ${getQuantityBgColor(productTotals.completedTotal, true)} sticky right-0 bg-emerald-50`}>
                      <span className={`font-bold text-sm ${getQuantityColor(productTotals.completedTotal, true)}`}>
                        {formatNumber(productTotals.completedTotal, isRtl)}
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
            
            <tr className="bg-amber-100 font-bold border-t-2 border-amber-300">
              <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300 sticky left-0 bg-amber-100">
                {isRtl ? 'الإجمالي' : 'Total'}
              </td>
              <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300 sticky left-[90px] bg-amber-100">
                -
              </td>
              <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300">
                -
              </td>
              
              {displayedChefs.map((chef) => {
                const totals = calculateChefTotals(chef);
                
                return (
                  <React.Fragment key={`total-${chef.id}`}>
                    <td className={`px-3 py-3 text-center border-l border-amber-300 ${getQuantityBgColor(totals.assignedTotal, false)}`}>
                      <span className={`font-bold text-sm ${getQuantityColor(totals.assignedTotal, false)}`}>
                        {formatNumber(totals.assignedTotal, isRtl)}
                      </span>
                    </td>
                    {showCompleted && (
                      <td className={`px-3 py-3 text-center border-r border-amber-300 ${getQuantityBgColor(totals.completedTotal, true)}`}>
                        <span className={`font-bold text-sm ${getQuantityColor(totals.completedTotal, true)}`}>
                          {formatNumber(totals.completedTotal, isRtl)}
                        </span>
                      </td>
                    )}
                  </React.Fragment>
                );
              })}
              
              {filteredChefsData.length > 10 && (
                <td className="px-3 py-3 text-center border-l border-amber-300 bg-amber-100"></td>
              )}
              
              <td className={`px-4 py-3 text-center border-l border-amber-300 bg-amber-200 ${showCompleted ? 'sticky right-[70px]' : 'sticky right-0'}`}>
                <span className="text-amber-800 font-bold text-sm">
                  {formatNumber(
                    displayedChefs.reduce((sum, chef) => sum + calculateChefTotals(chef).assignedTotal, 0),
                    isRtl
                  )}
                </span>
              </td>
              {showCompleted && (
                <td className="px-4 py-3 text-center bg-emerald-200 sticky right-0">
                  <span className="text-emerald-800 font-bold text-sm">
                    {formatNumber(
                      displayedChefs.reduce((sum, chef) => sum + calculateChefTotals(chef).completedTotal, 0),
                      isRtl
                    )}
                  </span>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDepartmentsTables = () => {
    const departmentsWithProducts = displayedDepartments.filter(dept => 
      dept.products && dept.products.length > 0
    );
    
    if (departmentsWithProducts.length === 0) {
      return (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isRtl ? 'لا توجد أقسام تحتوي على منتجات' : 'No departments with products found'}
          </h3>
          <p className="text-gray-600">
            {isRtl ? 'لا توجد بيانات أقسام تحتوي على منتجات متاحة' : 'No department data with products available'}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {departmentsWithProducts.map((department, deptIndex) => {
          const departmentChefs = getChefsByDepartment(department.name);
          const departmentProducts = getProductsByDepartment(department.name);
          const isExpanded = expandedDepartments.has(department.name);
          const departmentTotals = calculateDepartmentTotals(department.name);
          const completionRate = departmentTotals.assignedTotal > 0 
            ? (departmentTotals.completedTotal / departmentTotals.assignedTotal) * 100 
            : 0;
          
          return (
            <div key={department.name} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div 
                className="px-6 py-4 bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => toggleDepartment(department.name)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{fixArabicText(department.name)}</h3>
                      <p className="text-sm text-gray-600">
                        {departmentChefs.length} {isRtl ? 'شيف' : 'chef'} • 
                        {departmentProducts.length} {isRtl ? 'منتج' : 'product'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-900">
                        {formatNumber(departmentTotals.assignedTotal, isRtl)}
                      </div>
                      <div className="text-xs text-gray-600">
                        {isRtl ? 'مطلوب' : 'Assigned'}
                      </div>
                    </div>
                    {showCompleted && (
                      <>
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">
                            {formatNumber(departmentTotals.completedTotal, isRtl)}
                          </div>
                          <div className="text-xs text-gray-600">
                            {isRtl ? 'مكتمل' : 'Completed'}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">
                            {formatPercentage(completionRate, isRtl)}
                          </div>
                          <div className="text-xs text-gray-600">
                            {isRtl ? 'نسبة الإنجاز' : 'Completion Rate'}
                          </div>
                        </div>
                      </>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          exportDepartmentProducts(
                            department.name,
                            departmentProducts,
                            isRtl,
                            'pdf'
                          );
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
                        title={isRtl ? 'تصدير PDF' : 'Export PDF'}
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          exportDepartmentProducts(
                            department.name,
                            departmentProducts,
                            isRtl,
                            'xlsx'
                          );
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                        title={isRtl ? 'تصدير Excel' : 'Export Excel'}
                      >
                        <FileSpreadsheet className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              </div>
              
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 bg-gray-50">
                      <div className={`grid ${showCompleted ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <div className="text-center">
                            <div className="text-sm text-gray-600 mb-1">
                              {isRtl ? 'عدد المنتجات' : 'Products Count'}
                            </div>
                            <div className="text-2xl font-bold text-gray-900">
                              {departmentProducts.length}
                            </div>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <div className="text-center">
                            <div className="text-sm text-gray-600 mb-1">
                              {isRtl ? 'إجمالي مطلوب' : 'Total Assigned'}
                            </div>
                            <div className="text-2xl font-bold text-amber-600">
                              {formatNumber(departmentTotals.assignedTotal, isRtl)}
                            </div>
                          </div>
                        </div>
                        {showCompleted && (
                          <>
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <div className="text-center">
                                <div className="text-sm text-gray-600 mb-1">
                                  {isRtl ? 'إجمالي مكتمل' : 'Total Completed'}
                                </div>
                                <div className="text-2xl font-bold text-emerald-600">
                                  {formatNumber(departmentTotals.completedTotal, isRtl)}
                                </div>
                              </div>
                            </div>
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <div className="text-center">
                                <div className="text-sm text-gray-600 mb-1">
                                  {isRtl ? 'نسبة الإنجاز' : 'Completion Rate'}
                                </div>
                                <div className="text-2xl font-bold text-blue-600">
                                  {formatPercentage(completionRate, isRtl)}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      
                      {departmentProducts.length > 0 && (
                        <div className="mt-6">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">
                            {isRtl ? 'المنتجات في هذا القسم' : 'Products in this Department'}
                          </h4>
                          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center">
                                    {isRtl ? 'الكود' : 'Code'}
                                  </th>
                                  <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center">
                                    {isRtl ? 'اسم المنتج' : 'Product Name'}
                                  </th>
                                  <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center">
                                    {isRtl ? 'مطلوب' : 'Assigned'}
                                  </th>
                                  {showCompleted && (
                                    <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center">
                                      {isRtl ? 'مكتمل' : 'Completed'}
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {departmentProducts.map((product, idx) => {
                                  const productAssignedTotal = departmentChefs.reduce((sum, chef) => 
                                    sum + (chef.products && chef.products[product.id] ? chef.products[product.id].totalAssigned || 0 : 0), 0);
                                  const productCompletedTotal = departmentChefs.reduce((sum, chef) => 
                                    sum + (chef.products && chef.products[product.id] ? chef.products[product.id].totalCompleted || 0 : 0), 0);
                                  
                                  return (
                                    <tr key={product.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">
                                        {product.code || '-'}
                                      </td>
                                      <td className="px-4 py-3 text-center text-sm text-gray-900">
                                        {fixArabicText(product.name)}
                                      </td>
                                      <td className="px-4 py-3 text-center text-sm font-medium text-amber-600">
                                        {formatNumber(productAssignedTotal, isRtl)}
                                      </td>
                                      {showCompleted && (
                                        <td className="px-4 py-3 text-center text-sm font-medium text-emerald-600">
                                          {formatNumber(productCompletedTotal, isRtl)}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen p-4 md:p-6">
      <div className="max-w-full mx-auto">
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                {isRtl ? 'تقرير ملخص الإنتاج' : 'Production Summary Report'}
              </h1>
              <p className="text-gray-600 mt-1">
                {isRtl ? 'عرض المهام المطلوبة والمكتملة حسب الشيف أو القسم' : 'View assigned and completed tasks by chef or department'}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleRefresh}
                icon={<RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />}
                size="md"
                disabled={refreshing}
              >
                {refreshing ? (isRtl ? 'جارٍ التحديث...' : 'Refreshing...') : (isRtl ? 'تحديث' : 'Refresh')}
              </Button>
              
              <CompactExportDropdown
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                disabled={filteredChefsData.length === 0}
                isRtl={isRtl}
                showCompleted={showCompleted}
                onToggleShowCompleted={toggleShowCompleted}
              />
            </div>
          </div>
        </div>
        
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatsCard
              title={isRtl ? 'إجمالي الشيفات' : 'Total Chefs'}
              value={formatNumber(filteredChefsData.length, isRtl)}
              icon={<Users className="w-5 h-5" />}
              color="blue"
              loading={loading}
              isRtl={isRtl}
            />
            <StatsCard
              title={isRtl ? 'إجمالي المنتجات' : 'Total Products'}
              value={formatNumber(allProducts.length, isRtl)}
              icon={<Package className="w-5 h-5" />}
              color="emerald"
              loading={loading}
              isRtl={isRtl}
            />
            <StatsCard
              title={isRtl ? 'الكمية المطلوبة' : 'Total Assigned Quantity'}
              value={formatNumber(
                chefsData.reduce((sum, chef) => sum + (chef.totalAssignedQuantity || 0), 0),
                isRtl
              )}
              icon={<CheckCircle className="w-5 h-5" />}
              color="amber"
              loading={loading}
              isRtl={isRtl}
            />
            <StatsCard
              title={isRtl ? 'الكمية المكتملة' : 'Total Completed Quantity'}
              value={formatNumber(
                chefsData.reduce((sum, chef) => sum + (chef.totalCompletedQuantity || 0), 0),
                isRtl
              )}
              icon={<CheckCircle className="w-5 h-5" />}
              color="green"
              loading={loading}
              isRtl={isRtl}
            />
          </div>
        )}
        
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
                {isRtl ? 'عرض' : 'View'}
              </label>
              <div className="flex gap-2">
                {viewTypeOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setViewType(option.value as 'chefs' | 'departments')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all flex-1 ${
                      viewType === option.value
                        ? 'bg-gray-800 text-white shadow-sm'
                        : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    type="button"
                  >
                    {option.icon}
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
                {isRtl ? 'الفترة الزمنية' : 'Time Period'}
              </label>
              <Dropdown
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                icon={<Calendar className="w-4 h-4" />}
                isRtl={isRtl}
              />
            </div>
            
            {viewType === 'chefs' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
                  {isRtl ? 'الشيفات' : 'Chefs'}
                </label>
                <MultiSelectDropdown
                  value={selectedChefsMulti}
                  onChange={setSelectedChefsMulti}
                  options={chefMultiSelectOptions}
                  isRtl={isRtl}
                  placeholder={isRtl ? 'اختر الشيفات...' : 'Select chefs...'}
                />
              </div>
            )}
            
            {viewType === 'departments' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
                  {isRtl ? 'الأقسام' : 'Departments'}
                </label>
                <MultiSelectDropdown
                  value={selectedDepartmentsMulti}
                  onChange={setSelectedDepartmentsMulti}
                  options={departmentMultiSelectOptions}
                  isRtl={isRtl}
                  placeholder={isRtl ? 'اختر الأقسام...' : 'Select departments...'}
                />
              </div>
            )}
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
                {isRtl ? 'ترتيب المنتجات' : 'Sort Products'}
              </label>
              <Dropdown
                value={sortBy}
                onChange={setSortBy}
                options={sortOptions}
                icon={<SortAsc className="w-4 h-4" />}
                isRtl={isRtl}
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
              {isRtl ? 'بحث عن شيف' : 'Search for chef'}
            </label>
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isRtl ? 'ابحث باسم الشيف، البريد الإلكتروني...' : 'Search by chef name, email...'}
              isRtl={isRtl}
            />
          </div>
        </div>
        
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></div>
                <span className="text-xs text-gray-700">{isRtl ? 'مطلوب' : 'Assigned'}</span>
              </div>
              {showCompleted && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></div>
                  <span className="text-xs text-gray-700">{isRtl ? 'مكتمل' : 'Completed'}</span>
                </div>
              )}
              <div className="text-xs text-gray-500">
                {viewType === 'chefs' 
                  ? (isRtl ? `عرض حسب الشيفات` : `View by Chefs`)
                  : (isRtl ? `عرض إجماليات الأقسام` : `Showing department totals`)}
              </div>
            </div>
            
            {viewType === 'chefs' ? renderChefsTable() : renderDepartmentsTables()}
            
            <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-600">
                    {viewType === 'chefs' ? (
                      <>
                        {displayedChefs.length} {isRtl ? 'شيف' : 'chef'} • {allProducts.length} {isRtl ? 'منتج' : 'product'} • {displayedDepartments.length} {isRtl ? 'قسم' : 'department'}
                      </>
                    ) : (
                      <>
                        {displayedDepartments.length} {isRtl ? 'قسم' : 'department'} • {filteredChefsData.length} {isRtl ? 'شيف' : 'chef'} • {allProducts.length} {isRtl ? 'منتج' : 'product'}
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900">
                      {formatNumber(
                        chefsData.reduce((sum, chef) => sum + (chef.totalAssignedQuantity || 0), 0),
                        isRtl
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {isRtl ? 'إجمالي المطلوب' : 'Total Assigned'}
                    </div>
                  </div>
                  {showCompleted && (
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-900">
                        {formatNumber(
                          chefsData.reduce((sum, chef) => sum + (chef.totalCompletedQuantity || 0), 0),
                          isRtl
                        )}
                      </div>
                      <div className="text-xs text-gray-600">
                        {isRtl ? 'إجمالي المكتمل' : 'Total Completed'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductionReport;